import AppLoading from '../AppLoading/AppLoading';
import i18next from 'i18next';
import Intro from '../../Intro/Intro';
import ProgressBar from '../../ProgressBar/ProgressBar';
import SspProviderContext from '../../../utils/SspProvider/context';
import { FullWidthButton } from '../../AutostadtTabComponents/Button';
import { OverlayEnum } from '../../../enums';
import { setOverlay } from '../../../utils/SspProvider/dispatchFunctions';
import { useCachedVideo } from '../../../utils/hooks/useCachedVideo';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import type { AnimationEvent } from 'react';
import { markIntroDone } from '../../../App';

const finishIntro = () => {
    markIntroDone();
};
const TIMEOUT_SLOW_CONNECTION = 15_000;
const VIDEO_URL = '/autostadt_mood.mp4';

enum View {
    AppLoading,
    InitLoading,
    Intro
}

type TransitionTarget = 'intro';

export default function InitLoading(): JSX.Element {
    const store = useContext(SspProviderContext);

    const [isSlowConnection, setIsSlowConnection] = useState<boolean>(false);
    const [animate, setAnimate] = useState<boolean>(false);
    const [introSkipped, setIntroSkipped] = useState<boolean>(false);
    const [transitionTo, setTransitionTo] = useState<TransitionTarget | null>(null);
    const [view, setView] = useState<View | null>(View.InitLoading);
    const { cachedUrl: videoCamflight, mimeType: videoCamflightType } = useCachedVideo(store.state.content?.asModel.ref('driving_scene_asset').asAsset.url);
    const { cachedUrl: videoMood, mimeType: videoMoodType } = useCachedVideo(VIDEO_URL);

    const coverElementRef = useRef<HTMLDivElement>(null);
    const logoElementRef = useRef<HTMLDivElement>(null);

    const introShown = localStorage.getItem('introShown') === String(true);
    const introReady = store.state.isTranslated && !!store.state.content && !!videoCamflight && !!videoMood;

    if (store.state.overlay?.enum !== OverlayEnum.Loading) {
        setView(null);
    }

    const handleAnimationEnd = useCallback((e: AnimationEvent) => {
        if (e.animationName === 'init-loading-fade-in' && !animate) {
            setAnimate(true);
        }
        if ((e.target as HTMLElement).classList.contains('cover') && e.animationName === 'init-loading-fade-in') {
            setTransitionTo(null);
            setView(View.Intro);
        }
        if ((e.target as HTMLElement).classList.contains('logo') && e.animationName === 'init-loading-slide-out') {
            setView(View.AppLoading);
        }
    }, [animate]);

    const handleSkipIntro = () => {
        setIntroSkipped(true);
        setView(View.AppLoading);
    }

    const handleShowIntro = () => {
        setTransitionTo('intro');
        setView(View.InitLoading);
    };

    const handleIntroCanceled = () => {
        setView(View.AppLoading);
    };

    const handleUnmount = () => {
        setOverlay(store, null);
        setView(null);
        finishIntro();
    }

    useEffect(() => {
        if (introShown && !transitionTo && store.state.isTranslated) {
            logoElementRef.current?.classList.add('slide-out');
        } else if (introReady) {
            if (!introShown || transitionTo === 'intro') {
                coverElementRef.current?.classList.add('fade-in');
            }
        }
    }, [introShown, introReady, transitionTo, store.state.isTranslated])

    useEffect(() => {
        const timerId = setTimeout(() => {
            setIsSlowConnection(true);
        }, TIMEOUT_SLOW_CONNECTION);

        if (introReady) {
            clearTimeout(timerId);
        }

        return () => {
            clearTimeout(timerId)
        };
    }, [introReady, introShown, transitionTo]);

    if (view === View.InitLoading) {
        return (
            <div className='loading-init-container'>
                <div className='loading-init' onAnimationEnd={handleAnimationEnd}>
                    <div className='cover' ref={coverElementRef} onAnimationEnd={handleAnimationEnd}></div>
                    <div className={`top${introShown && !transitionTo ? ' expand' : ''}`}>
                        <div className='logo' ref={logoElementRef} onAnimationEnd={handleAnimationEnd}></div>
                    </div>
                    {(!introShown || transitionTo) &&
                        <div className='center'>
                            <ProgressBar
                                animate={animate}
                            />
                        </div>
                    }
                    {!introShown && !transitionTo && isSlowConnection && store.state.isTranslated &&
                        <div className='bottom'>
                            <p>{i18next.t('InternetConnectionSlow')}</p>
                            <FullWidthButton
                                color='white'
                                onClick={handleSkipIntro}
                                text={i18next.t('IntroSkip')}
                                type='primary'
                            />
                        </div>
                    }
                </div>
            </div>
        );
    }

    if (view === View.AppLoading) {
        return <AppLoading
            introViewable={!introSkipped && introReady}
            onEnded={handleUnmount}
            onShowIntro={handleShowIntro}
        />
    }

    if (view === View.Intro) {
        return <Intro
            onEnded={handleUnmount}
            onIntroCanceled={handleIntroCanceled}
            videos={[
                { src: videoMood ?? '', type: videoMoodType ? videoMoodType : undefined },
                { src: videoCamflight ?? '', type: videoCamflightType ? videoCamflightType : undefined }
            ]}
        />
    }

    return <></>;

}
